import React from 'react';
import { Dumbbell, Mail, Phone } from 'lucide-react';

const translations = {
  de: {
    contactTitle: 'Kontaktieren Sie uns:',
    contactSubtitle: 'Bitte kontaktieren Sie unseren Kundenservice unter den folgenden Daten.',
    legal: 'Mit FitXcel erhalten Sie ein individuelles, AI-gestuetztes Trainingsprogramm, das Sie ganz nach Ihren Wuenschen zusammenstellen koennen und das sowohl fuer Einsteiger als auch fuer Fortgeschrittene geeignet ist. Dank der leistungsstarken AI gehoeren zu den Trainingsprogrammen ausserdem ein sehr detaillierter und ausgewogener Ernaehrungs- und Essensplan mit einer Vielzahl von Rezepten, angepasst an alle, ob Veganer, Vegetarier oder Allesesser. Der Zugang fuer 4 Wochen kostet einmalig 15 Euro (inkl. MwSt.).',
    customerService: 'Kundenservice:',
    rights: 'Alle Rechte vorbehalten.',
    quickLinks: 'Schnelllinks',
    legalLinks: 'Rechtliches',
    home: 'Startseite',
    games: 'Programme',
    features: 'Vorteile',
    faq: 'FAQ',
    terms: 'AGB',
    imprint: 'Impressum',
    privacy: 'Datenschutz',
    contact: 'Kontakt',
    provider: 'Diensteanbieter:',
  },
  en: {
    contactTitle: 'Contact us:',
    contactSubtitle: 'Please contact our support team using the details below.',
    legal: 'FitXcel provides a personalized AI-powered training program and a detailed nutrition plan with recipes for vegan, vegetarian and omnivore preferences. Access for 4 weeks costs a one-time 15 EUR (incl. VAT).',
    customerService: 'Customer Service:',
    rights: 'All rights reserved.',
    quickLinks: 'Quick Links',
    legalLinks: 'Legal',
    home: 'Home',
    games: 'Programs',
    features: 'Features',
    faq: 'FAQ',
    terms: 'Terms',
    imprint: 'Imprint',
    privacy: 'Privacy',
    contact: 'Contact',
    provider: 'Service provider:',
  }
};

function Footer({ language }) {
  const t = translations[language];

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer id="footer" className="relative bg-darker border-t border-slate-800">
      {/* Gradient top border */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary to-transparent"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Section */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-6">
              <div className="relative">
                <div className="w-12 h-12 bg-gradient-to-br from-primary via-secondary to-accent rounded-xl flex items-center justify-center">
                  <Dumbbell className="w-7 h-7 text-white" />
                </div>
              </div>
              <div>
                <span className="font-heading text-xl font-bold text-white tracking-wider">
                  Fit<span className="text-accent">Xcel</span>
                </span>
                <span className="block text-xs text-muted font-body tracking-widest">24kitchenai.com</span>
              </div>
            </div>
            
            {/* Legal Notice - Prominent */}
            <div className="bg-surface/50 border border-slate-700 rounded-xl p-6 mb-6">
              <p className="text-slate-300 font-body text-base leading-relaxed">
                {t.legal}
              </p>
            </div>

            <p className="text-slate-500 font-body text-sm">
              {t.provider} <span className="text-accent">Business Fleet s.r.o.</span><br />
              {t.customerService} <span className="text-accent">fitxcel.de@silverlines.info</span> | <span className="text-accent">08005895405</span>
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-heading font-bold text-white text-lg mb-6">{t.quickLinks}</h3>
            <ul className="space-y-3">
              {[
                { label: t.home, id: 'hero' },
                { label: t.features, id: 'features' },
                { label: t.games, id: 'games' },
                { label: t.faq, id: 'faq' },
              ].map((link) => (
                <li key={link.id}>
                  <button
                    onClick={() => scrollToSection(link.id)}
                    className="text-slate-400 hover:text-white font-body transition-colors duration-200 flex items-center gap-2 group"
                  >
                    <span className="w-1.5 h-1.5 bg-primary rounded-full group-hover:bg-accent transition-colors"></span>
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>

            <h3 className="font-heading font-bold text-white text-lg mt-10 mb-4">{t.legalLinks}</h3>
            <ul className="space-y-3">
              {[
                { label: t.terms, href: '/terms.html' },
                { label: t.imprint, href: '/impressum.html' },
                { label: t.contact, href: '/kontakt.html' },
                { label: t.privacy, href: '/privacy.html' },
              ].map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    className="text-slate-400 hover:text-white font-body transition-colors duration-200 flex items-center gap-2 group"
                  >
                    <span className="w-1.5 h-1.5 bg-primary rounded-full group-hover:bg-accent transition-colors"></span>
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Section */}
          <div>
            <h3 className="font-heading font-bold text-white text-lg mb-6">{t.contactTitle}</h3>
            <p className="text-slate-400 font-body text-sm mb-6">
              {t.contactSubtitle}
            </p>
            
            <div className="space-y-4">
              <a 
                href="mailto:fitxcel.de@silverlines.info" 
                className="flex items-center gap-3 text-slate-400 hover:text-accent transition-colors group"
              >
                <div className="w-10 h-10 bg-surface border border-slate-700 rounded-lg flex items-center justify-center group-hover:border-accent transition-colors">
                  <Mail className="w-5 h-5" />
                </div>
                <span className="font-body">fitxcel.de@silverlines.info</span>
              </a>
              
              <a 
                href="tel:08005895405" 
                className="flex items-center gap-3 text-slate-400 hover:text-accent transition-colors group"
              >
                <div className="w-10 h-10 bg-surface border border-slate-700 rounded-lg flex items-center justify-center group-hover:border-accent transition-colors">
                  <Phone className="w-5 h-5" />
                </div>
                <span className="font-body">08005895405</span>
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-slate-800">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 font-body text-sm">
              © {new Date().getFullYear()} 24kitchenai.com. {t.rights}
            </p>
            
            {/* Decorative element */}
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
              <div className="w-2 h-2 bg-secondary rounded-full animate-pulse animation-delay-200"></div>
              <div className="w-2 h-2 bg-accent rounded-full animate-pulse animation-delay-400"></div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
